import DeleteOutline from '@mui/icons-material/DeleteOutline';
import PauseCircleOutline from '@mui/icons-material/PauseCircleOutline';
import {
    Alert,
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    IconButton,
    MenuItem,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    TextField,
    Tooltip,
    Typography,
} from '@mui/material';
import React, { useEffect, useMemo, useState } from 'react';
import Swal from 'sweetalert2';
import api from '../../api';
import {
    PRODUCTIVITY_REASONS,
    formatRestrictionDate,
    restrictionsForPrinter,
    todayISODate,
} from '../../helpers/printerProductivity';

function apiErrorText(error) {
    const data = error?.response?.data;
    const errors = data?.errors;
    if (errors && typeof errors === 'object') {
        const message = Object.values(errors).find(Boolean);
        if (message) {
            return String(message);
        }
    }
    return data?.error || 'Tente novamente.';
}

export default function PrinterProductivityDialog({
    open,
    printer,
    restrictions = [],
    onClose,
    onChanged,
}) {
    const printerId = printer?.printer_uuid || printer?.id || '';
    const history = useMemo(
        () => restrictionsForPrinter(restrictions, printerId),
        [restrictions, printerId],
    );
    const current = history.find((row) => row.open) || null;
    const today = todayISODate();

    const [reason, setReason] = useState(PRODUCTIVITY_REASONS[0].value);
    const [startedOn, setStartedOn] = useState(today);
    const [notes, setNotes] = useState('');
    const [endedOn, setEndedOn] = useState(today);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState('');

    useEffect(() => {
        if (!open) {
            return;
        }
        setError('');
        setEndedOn(todayISODate());
        if (current) {
            return;
        }
        setReason(PRODUCTIVITY_REASONS[0].value);
        setStartedOn(todayISODate());
        setNotes('');
    }, [open, current, printerId]);

    const saveOpen = async () => {
        setSaving(true);
        setError('');
        try {
            await api.post(`printers/${printerId}/productivity-restrictions`, {
                reason,
                started_on: startedOn,
                notes,
            });
            onChanged?.();
        } catch (err) {
            setError(apiErrorText(err));
        } finally {
            setSaving(false);
        }
    };

    const endRestriction = async () => {
        if (!current) {
            return;
        }
        setSaving(true);
        setError('');
        try {
            await api.post(`printers/${printerId}/productivity-restrictions/${current.id}/end`, {
                ended_on: endedOn,
            });
            onChanged?.();
            onClose?.();
        } catch (err) {
            setError(apiErrorText(err));
        } finally {
            setSaving(false);
        }
    };

    const removeRestriction = async (row) => {
        const confirmed = await Swal.fire({
            icon: 'warning',
            title: 'Remover esta restrição?',
            text: row.open
                ? 'A impressora volta a poder produzir. O registro sai do histórico.'
                : 'O registro sai do histórico.',
            showCancelButton: true,
            confirmButtonText: 'Remover',
            cancelButtonText: 'Cancelar',
            didOpen: () => {
                const container = Swal.getContainer();
                if (container) {
                    container.style.zIndex = '2000';
                }
            },
        });
        if (!confirmed.isConfirmed) {
            return;
        }
        setSaving(true);
        setError('');
        try {
            await api.delete(`printers/${printerId}/productivity-restrictions/${row.id}`);
            onChanged?.();
        } catch (err) {
            setError(apiErrorText(err));
        } finally {
            setSaving(false);
        }
    };

    return (
        <Dialog open={open} onClose={saving ? undefined : onClose} fullWidth maxWidth="md" disableEnforceFocus>
            <DialogTitle>
                {printer?.name || 'Impressora'}
                {' · '}
                {current ? 'Não produtiva' : 'Marcar como não produtiva'}
            </DialogTitle>
            <DialogContent dividers>
                <Stack spacing={2}>
                    {error ? <Alert severity="error">{error}</Alert> : null}
                    {current ? null : (
                        <>
                            <TextField
                                select
                                label="Motivo"
                                value={reason}
                                onChange={(event) => setReason(event.target.value)}
                                fullWidth
                            >
                                {PRODUCTIVITY_REASONS.map((item) => (
                                    <MenuItem key={item.value} value={item.value}>{item.label}</MenuItem>
                                ))}
                            </TextField>
                            <TextField
                                label="Início"
                                type="date"
                                value={startedOn}
                                onChange={(event) => setStartedOn(event.target.value)}
                                InputLabelProps={{ shrink: true }}
                                inputProps={{ max: today }}
                                fullWidth
                                required
                            />
                            <TextField
                                label="Observação"
                                value={notes}
                                onChange={(event) => setNotes(event.target.value)}
                                fullWidth
                                multiline
                                minRows={2}
                            />
                        </>
                    )}

                    {history.length === 0 ? null : (
                        <>
                            <Typography variant="subtitle2">Histórico</Typography>
                            <Table size="small">
                                <TableHead>
                                    <TableRow>
                                        <TableCell>Motivo</TableCell>
                                        <TableCell>Início</TableCell>
                                        <TableCell>Fim</TableCell>
                                        <TableCell>Observação</TableCell>
                                        <TableCell align="right" />
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {history.map((row) => (
                                        <TableRow key={row.id}>
                                            <TableCell>{row.reason_label || row.reason}</TableCell>
                                            <TableCell>{formatRestrictionDate(row.started_on)}</TableCell>
                                            <TableCell>{row.open ? 'Em aberto' : formatRestrictionDate(row.ended_on)}</TableCell>
                                            <TableCell>
                                                <Stack spacing={1} alignItems="flex-start">
                                                    <Typography variant="body2">{row.notes || '—'}</Typography>
                                                    {row.open ? (
                                                        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems={{ sm: 'center' }}>
                                                            <TextField
                                                                label="Fim"
                                                                type="date"
                                                                size="small"
                                                                value={endedOn}
                                                                onChange={(event) => setEndedOn(event.target.value)}
                                                                InputLabelProps={{ shrink: true }}
                                                                inputProps={{
                                                                    min: String(row.started_on || '').slice(0, 10),
                                                                    max: today,
                                                                }}
                                                            />
                                                            <Button
                                                                variant="contained"
                                                                size="small"
                                                                onClick={endRestriction}
                                                                disabled={saving || !endedOn}
                                                            >
                                                                Voltar a produzir
                                                            </Button>
                                                        </Stack>
                                                    ) : null}
                                                </Stack>
                                            </TableCell>
                                            <TableCell align="right">
                                                <Tooltip title="Remover registro">
                                                    <IconButton
                                                        size="small"
                                                        color="error"
                                                        aria-label="remover restrição"
                                                        disabled={saving}
                                                        onClick={() => removeRestriction(row)}
                                                    >
                                                        <DeleteOutline fontSize="small" />
                                                    </IconButton>
                                                </Tooltip>
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </>
                    )}
                </Stack>
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose} disabled={saving}>Fechar</Button>
                {current ? null : (
                    <Button
                        variant="contained"
                        onClick={saveOpen}
                        disabled={saving || !startedOn}
                        startIcon={<PauseCircleOutline />}
                    >
                        Marcar como não produtiva
                    </Button>
                )}
            </DialogActions>
        </Dialog>
    );
}
